// Telling one of your own cards from another.
//
// Every card you own carries the same name, because they are all you. What
// makes them different is which hat you are wearing — the company, and the job
// you do there. So that pair is both how a card should be LABELLED and how a
// redundant one is RECOGNISED: a second card for the same company and the same
// title is not a second card, it is the first one made twice.
//
// Deliberately not part of it:
//   - the name, which is constant and would make every card look identical;
//   - the phone, email and photo, which legitimately differ between two real
//     cards for the same role (a direct line on one, a switchboard on another)
//     and legitimately match across two genuinely different roles.

/** A card, as far as identity is concerned. */
export interface CardLike {
  name?: string;
  title?: string;
  company?: string;
  publicId?: string;
}

/** Lowercase, collapse runs of whitespace, drop surrounding punctuation. So
 *  "Northwind  Studio" and "northwind studio." are the same company. */
function norm(s: string | undefined): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/[\s\u00A0]+/g, ' ')
    .replace(/^[\s\u00A0.,;:\u2013\u2014-]+|[\s\u00A0.,;:\u2013\u2014-]+$/g, '')
    .trim();
}

/**
 * The pair that identifies a card among your own.
 *
 * Empty when there is neither a company nor a title — a card with only a name
 * cannot be told apart from another card with only a name, and calling those
 * duplicates would block somebody part-way through filling in their second one.
 */
export function cardKey(c: CardLike): string {
  const k = `${norm(c.company)}|${norm(c.title)}`;
  return k === '|' ? '' : k;
}

/**
 * What to call this card in a list.
 *
 * Company first, because that is what people reach for — "my Northwind card" —
 * with the title after it to separate two roles at the same place. Falls back
 * through whatever exists rather than showing an empty chip.
 */
export function cardLabel(c: CardLike, fallback = 'Card'): string {
  const company = (c.company ?? '').trim();
  const title = (c.title ?? '').trim();
  if (company && title) return `${company} · ${title}`;
  return company || title || (c.name ?? '').trim() || fallback;
}

/**
 * The card among `others` that this one would duplicate, if any.
 *
 * Compared by key, so it catches the case that actually happens: somebody makes
 * a second card for the same job because they forgot, or because they could not
 * find the first one. It ignores a card with the same publicId, which is the
 * card being edited rather than a clash.
 */
export function findDuplicate<T extends CardLike>(draft: CardLike, others: T[]): T | null {
  const key = cardKey(draft);
  if (!key) return null;
  return others.find((o) => o.publicId !== draft.publicId && cardKey(o) === key) ?? null;
}
